/**
 * Pure derivation rules ported from design handoff dc.html Component logic.
 */

import type {
  DriveSubdomainEdge,
  DriveSubdomainNode,
  GuidanceHiFiModel,
  GuidanceNodeStatus,
  GuidanceRollupCounts,
  GuidanceTreeNode,
  GuidanceWeekRange,
} from './types';
import { isGuidanceNodeCollapsible } from './guidanceTreeUi';
import { weekIndexToDisplayDate } from './guidanceViewport';
import { guidanceStatusFromPhrase } from '../../../utils/statusPhrasePlane';

/** Original layout total: 400 + (14 × 56) = 1184px — preserve when reallocating tree vs chart. */
const GUIDANCE_LEGACY_TREE_WIDTH_PX = 400;
const GUIDANCE_LEGACY_CHART_WIDTH_PX = 784;
const GUIDANCE_TREE_WIDTH_PX = 560;
const GUIDANCE_CHART_WIDTH_PX = GUIDANCE_LEGACY_TREE_WIDTH_PX + GUIDANCE_LEGACY_CHART_WIDTH_PX - GUIDANCE_TREE_WIDTH_PX;
const GUIDANCE_WEEK_COUNT = 12;
const GUIDANCE_WEEK_WIDTH_PX = GUIDANCE_CHART_WIDTH_PX / GUIDANCE_WEEK_COUNT;

export function ranksFromSubdomainEdges(
  nodes: Pick<DriveSubdomainNode, 'id'>[],
  edges: DriveSubdomainEdge[]
): Record<string, number> {
  const forward = edges.filter((e) => !e.back);
  const rank: Record<string, number> = {};
  nodes.forEach((n) => {
    rank[n.id] = 0;
  });
  for (let iter = 0; iter < nodes.length; iter += 1) {
    forward.forEach((e) => {
      if (rank[e.to] !== undefined && rank[e.from] !== undefined && rank[e.to] < rank[e.from] + 1) {
        rank[e.to] = rank[e.from] + 1;
      }
    });
  }
  return rank;
}

export function sequentialSubdomainEdges(subdomainIds: string[]): DriveSubdomainEdge[] {
  const edges: DriveSubdomainEdge[] = [];
  for (let i = 1; i < subdomainIds.length; i += 1) {
    edges.push({ from: subdomainIds[i - 1]!, to: subdomainIds[i]! });
  }
  return edges;
}

export function proxyWeekRangeFromIndex(index: number, span = 2, slack = 1.5): GuidanceWeekRange {
  const s = index * span;
  return { s, e: s + span, l: s + span + slack };
}

export function statusFromPhrase(phrase: string): GuidanceNodeStatus {
  return guidanceStatusFromPhrase(phrase);
}

export function rollupCounts(open: number, total: number, eligible = 0): GuidanceRollupCounts {
  const done = Math.max(0, total - open - eligible);
  return { done, open, eligible };
}

export interface FlatGuidanceRow {
  node: GuidanceTreeNode;
  depth: number;
  isCollapsible: boolean;
  isCollapsed: boolean;
}

export function flattenGuidanceTree(
  root: GuidanceTreeNode,
  collapsed: Record<string, boolean>
): FlatGuidanceRow[] {
  const rows: FlatGuidanceRow[] = [];
  const seenIds = new Set<string>();
  const walk = (node: GuidanceTreeNode, depth: number) => {
    if (seenIds.has(node.id)) {
      return;
    }
    seenIds.add(node.id);
    const kids = node.children ?? [];
    const isCollapsible = isGuidanceNodeCollapsible(node);
    const isCollapsed = isCollapsible ? Boolean(collapsed[node.id]) : false;
    rows.push({ node, depth, isCollapsible, isCollapsed });
    if (isCollapsible && !isCollapsed) {
      kids.forEach((child) => walk(child, depth + 1));
    }
  };
  walk(root, 0);
  return rows;
}

export function barGeometry(range: GuidanceWeekRange, weekWidth = GUIDANCE_WEEK_WIDTH_PX) {
  const left = range.s * weekWidth;
  const earlyWidth = (range.e - range.s) * weekWidth;
  const tailWidth = (range.l - range.e) * weekWidth;
  return { left, earlyWidth, tailWidth, totalWidth: earlyWidth + tailWidth };
}

export function projectedMarkWeek(range: GuidanceWeekRange): number {
  return range.e + 0.5 * (range.l - range.e);
}

export function committedMarkWeek(range: GuidanceWeekRange): number {
  return range.e + 0.85 * (range.l - range.e);
}

export function deriveGuidanceSummary(root: GuidanceTreeNode): GuidanceHiFiModel['summary'] {
  const waypoints: GuidanceTreeNode[] = [];
  const collect = (n: GuidanceTreeNode) => {
    if (n.type === 'waypoint') waypoints.push(n);
    (n.children ?? []).forEach(collect);
  };
  collect(root);

  const sessionRange = root.range;
  const critical = waypoints.filter((a) => a.critical);
  const leverage = waypoints.filter((a) => a.leverage);
  const blockers = waypoints.filter((a) => a.blocker);

  return {
    whenLabel: 'When will it land',
    whenRange: `${weekIndexToDisplayDate(sessionRange.s)} – ${weekIndexToDisplayDate(sessionRange.l)}`,
    criticalPathWeeks: Math.max(0, sessionRange.l - sessionRange.s),
    leverageCount: leverage.length,
    blockerCount: blockers.length,
    scopeLabel: `${waypoints.length} waypoint${waypoints.length === 1 ? '' : 's'} on this session`,
  };
}

export function guidanceLayoutConstants() {
  return {
    weekCount: GUIDANCE_WEEK_COUNT,
    weekWidthPx: GUIDANCE_WEEK_WIDTH_PX,
    treeColumnWidthPx: GUIDANCE_TREE_WIDTH_PX,
    rowHeightPx: 46,
    barHeightPx: 18,
  };
}

/** Re-weight opacity for System↔Me overlay (design: ~40% dim). */
export function personalOverlayOpacity(mine: boolean | undefined, mode: 'system' | 'me'): number {
  if (mode === 'system') return 1;
  return mine ? 1 : 0.4;
}
